import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../../axiosInstance";
import { useTranslation } from "react-i18next";

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [newSpeakers, setNewSpeakers] = useState([]);
  const [mostViewedSpeakers, setMostViewedSpeakers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);

  // Fetch speakers
  useEffect(() => {
    const fetchNewSpeakers = async () => {
      try {
        const response = await axiosInstance.get(
          "/api/v1/users/speakers/home?type=1"
        );
        setNewSpeakers(response.data?.items?.slice(0, 5) || []);
      } catch (error) {
        console.error("Error fetching new speakers:", error);
      }
    };

    const fetchMostViewedSpeakers = async () => {
      try {
        const response = await axiosInstance.get(
          "/api/v1/users/speakers/home?type=2"
        );
        setMostViewedSpeakers(response.data?.items?.slice(0, 4) || []);
      } catch (error) {
        console.error("Error fetching most viewed speakers:", error);
      }
    };

    fetchNewSpeakers();
    fetchMostViewedSpeakers();
  }, []);

  // Auto slide every 5 seconds
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) =>
        mostViewedSpeakers.length ? (prev + 1) % mostViewedSpeakers.length : 0
      );
    }, 5000);

    return () => clearInterval(timerRef.current);
  }, [mostViewedSpeakers]);

  // Navigate handlers
  const goToSlide = (index) => {
    setCurrentIndex(index);
    clearInterval(timerRef.current);
    // Restart timer after manual change
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) =>
        mostViewedSpeakers.length ? (prev + 1) % mostViewedSpeakers.length : 0
      );
    }, 5000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? mostViewedSpeakers.length - 1 : prev - 1
    );
    clearInterval(timerRef.current);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % mostViewedSpeakers.length);
    clearInterval(timerRef.current);
  };

  return (
    <section className="hero-light py-5">
      <div className="hero-pattern"></div>
      <div className="container py-5">
        <div className="row align-items-center">
          <div className="col-lg-7 mb-5 mb-lg-0">
            <h1 className="display-4 fw-bold mb-3 text-primary-custom hero-title">
              {t("hero.title")}
            </h1>
            <p className="lead mb-5 text-dark hero-subtitle">
              {t("hero.subtitle")}
            </p>

            {/* Recently Joined Speakers */}
            <div className="recently-joined-section">
              <div className="mb-3 d-inline-block">
                <span className="badge bg-primary-custom py-2 px-3 rounded-pill fs-6">
                  {t("hero.recentlyJoined")}
                </span>
              </div>
              <div className="speaker-avatars d-flex flex-wrap justify-content-center justify-content-lg-start">
                {newSpeakers.map((speaker, index) => (
                  <div
                    key={speaker.id}
                    className="speaker-avatar-wrapper"
                    style={{
                      marginRight: index !== 0 ? "-15px" : "0",
                      zIndex: 5 - index,
                    }}
                  >
                    <a href={`/speaker-detail/${speaker.id}`}>
                      <img
                        src={speaker.profileImage}
                        className="speaker-avatar"
                        alt={
                          isArabic
                            ? speaker.arabicFullName
                            : speaker.englishFullName
                        }
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid white",
                          boxShadow: "0 0 5px rgba(0,0,0,0.2)",
                        }}
                        loading="lazy"
                      />
                      <div className="speaker-name-tooltip">
                        {isArabic
                          ? speaker.arabicFullName
                          : speaker.englishFullName}
                      </div>
                    </a>
                  </div>
                ))}

                <div
                  className="speaker-avatar-wrapper more-speakers"
                  style={{ marginRight: "-15px", zIndex: 0 }}
                >
                  <a href="/speakers">
                    <div
                      className="speaker-avatar more-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        backgroundColor: "#007bff",
                        color: "white",
                        fontSize: "24px",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                    >
                      <span>+</span>
                    </div>
                    <div className="speaker-name-tooltip">
                      {t("hero.moreSpeakers")}
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Carousel Section */}
          <div className="col-lg-5 text-center">
            <div className="featured-speaker-slider">
              <div className="last-joined-heading mb-4">
                <span className="badge bg-primary-custom py-2 px-3 rounded-pill fs-6">
                  {t("hero.featuredSpeakers")}
                </span>
              </div>

              <div
                className="custom-carousel position-relative"
                style={{ maxWidth: 300, margin: "0 auto" }}
              >
                {mostViewedSpeakers.length === 0 && <p>{t("loading")}</p>}

                {mostViewedSpeakers.map((speaker, index) => (
                  <div
                    key={speaker.id}
                    className={`carousel-slide ${
                      index === currentIndex ? "active" : ""
                    }`}
                    style={{
                      display: index === currentIndex ? "block" : "none",
                      transition: "opacity 0.8s ease-in-out",
                    }}
                  >
                    <a
                      href={`/speaker-detail/${speaker.id}`}
                      className="text-decoration-none"
                    >
                      <div className="featured-speaker-minimal">
                        <div className="featured-speaker-portrait position-relative">
                          <img
                            src={speaker.profileImage}
                            alt={
                              isArabic
                                ? speaker.arabicFullName
                                : speaker.englishFullName
                            }
                            loading="lazy"
                            style={{
                              width: "250px",
                              height: "250px",
                              objectFit: "cover",
                              borderRadius: "15px",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            }}
                          />

                          {/* Overlay for Name & Specialities */}
                          <div
                            className="position-absolute bottom-0 start-0 w-100 text-center"
                            style={{
                              background: "rgba(0,0,0,0.5)",
                              padding: "10px",
                              borderBottomLeftRadius: "15px",
                              borderBottomRightRadius: "15px",
                            }}
                          >
                            <h5 className="text-white mb-1">
                              {isArabic
                                ? speaker.arabicFullName
                                : speaker.englishFullName}
                            </h5>
                            {speaker.speakerSpecialies?.map((speciality, i) => (
                              <p
                                key={i}
                                className="mb-0 text-white"
                                style={{ fontSize: "13px" }}
                              >
                                {isArabic
                                  ? speciality.speciality.arabicName
                                  : speciality.speciality.englishName}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                ))}

                {/* Controls */}
                <button
                  onClick={prevSlide}
                  aria-label={t("carousel.prev")}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    fontSize: "30px",
                    cursor: "pointer",
                    color: "#007bff",
                    userSelect: "none",
                  }}
                >
                  &#8249;
                </button>
                <button
                  onClick={nextSlide}
                  aria-label={t("carousel.next")}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: 0,
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    fontSize: "30px",
                    cursor: "pointer",
                    color: "#007bff",
                    userSelect: "none",
                  }}
                >
                  &#8250;
                </button>

                {/* Indicators */}
                <div
                  className="carousel-indicators"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 15,
                    gap: 8,
                  }}
                >
                  {mostViewedSpeakers.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      aria-label={`Slide ${index + 1}`}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        border: "none",
                        backgroundColor:
                          index === currentIndex ? "#007bff" : "#ccc",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
